/**
 * The queue's actions, the same for the screens whether the queue runs on the queue service (dabbeh)
 * (DABBEH_API_URL set) or on this device only (prototype). The service is the source of truth when on:
 * every action calls it first and the store only mirrors its answer.
 */
import { book } from '@/common/apis/services/booking/book';
import { ClinicStatus } from '@/common/constants/status/clinicStatus';
import { useUserInfoStore, type UserInfo } from '@/modules/login/store/userInfo';
import { useEffect, useState } from 'react';
import { QueueApiError, isQueueApi, queueApi, toMembership, type ServerSeat } from './api';
import {
  clearPendingBooking,
  findBookedAppointment,
  readPendingBookings,
  savePendingBooking,
  type BookedAppointment,
  type PendingBooking,
} from './pendingBooking';
import { isDabbehRolloutSlug, markDabbehUsed } from './rollout';
import { useDabbehStore } from './store/dabbehStore';
import type { DabbehDoctor, DabbehMembership, DabbehSubscription } from './types';

/** Bumped by every change this device makes, so a slower sync that started before it does not undo it. */
let changes = 0;

/** The signed-in account's id, if the site has it (the seats are kept for that account). */
const currentUserId = () => {
  const id = useUserInfoStore.getState?.()?.info?.id;
  return id ? String(id) : null;
};

/** The same slot: the same time — or, when the clinic gave no time, the same wording. */
const sameVisit = (a: DabbehMembership['offer'], b: DabbehMembership['offer']) =>
  a?.visitAt || b?.visitAt ? a?.visitAt === b?.visitAt : (a?.visitLabel ?? '') === (b?.visitLabel ?? '');

/** A seat joined on another device: the doctor's name from the profile address until the profile is opened here. */
export const doctorFromSlug = (slug: string): DabbehDoctor => {
  let name = slug;
  try {
    name = decodeURIComponent(slug);
  } catch {
    // keep as is
  }
  return { slug, name: name.replace(/^دکتر-/, '').replace(/-\d+$/, '').replace(/-/g, ' ') };
};

const mirror = (seat: ServerSeat, viaSubscription?: boolean) => {
  const state = useDabbehStore.getState();
  const known = state.memberships[seat.slug];
  const doctor = known?.doctor ?? (state.draft?.slug === seat.slug ? state.draft : doctorFromSlug(seat.slug));
  const membership = toMembership(seat, doctor, viaSubscription ?? known?.viaSubscription ?? false);
  changes += 1;
  const ownerId = currentUserId();
  useDabbehStore.setState({ memberships: { ...useDabbehStore.getState().memberships, [seat.slug]: membership }, ...(ownerId ? { ownerId } : {}) });
  markDabbehUsed();
  return membership;
};

/** This doctor's seat as the service has it now (mirrored into the store), or null when there is none. */
const latestSeat = async (slug: string) => {
  const seats = await queueApi.mySeats();
  const found = seats.find(item => item.slug === slug);
  return found ? mirror(found) : null;
};

/** The patient's seats from the service (rollout doctors only); a no-op in the prototype. */
export const syncQueueSeats = async () => {
  if (!isQueueApi()) return;
  const startedAt = changes;
  const seats = await queueApi.mySeats();
  if (startedAt !== changes) return;
  if (seats.some(seat => isDabbehRolloutSlug(seat.slug))) markDabbehUsed();
  const state = useDabbehStore.getState();
  state.syncSeats(
    seats
      .filter(seat => isDabbehRolloutSlug(seat.slug))
      .map(seat => {
        const known = state.memberships[seat.slug];
        const doctor = known?.doctor ?? (state.draft?.slug === seat.slug ? state.draft : doctorFromSlug(seat.slug));
        return toMembership(seat, doctor, known?.viaSubscription ?? false);
      }),
    currentUserId(),
  );
};

/**
 * A shared phone: once the site knows who is signed in, seats synced for another account — or any seat once
 * signed out — leave this device, with the slots still waiting for `booked`. The prototype (no service)
 * keeps its on-device seats.
 */
export const forgetOtherAccount = (userId: string | null) => {
  if (!isQueueApi()) return false;
  const forgotten = useDabbehStore.getState().forgetOtherAccount(userId);
  if (forgotten) {
    changes += 1;
    clearPendingBooking();
  }
  return forgotten;
};

/** Offers whose `booked` is on its way right now (the list and the periodic sync may both find one). */
const reporting = new Set<number>();

/** Tells the service the slot was booked; on success the record is forgotten (and the seat ends here too). */
const reportBooked = async (offerId: number, bookId: string, slug: string) => {
  if (reporting.has(offerId)) return false;
  reporting.add(offerId);
  try {
    await queueApi.booked(offerId, bookId);
    clearPendingBooking(offerId);
    const seat = useDabbehStore.getState().memberships[slug];
    if (seat?.offer?.id === offerId) {
      changes += 1;
      useDabbehStore.getState().leave(slug);
    }
    return true;
  } catch {
    // kept: tried again on the next sync or visit of «نوبت‌های من» (idempotent on the service)
    return false;
  } finally {
    reporting.delete(offerId);
  }
};

/** The held slot of a seat as a pending record (null without what the service and the list need). */
const pendingOf = (seat: DabbehMembership, bookId?: string): PendingBooking | null => {
  const offer = seat.offer;
  if (!offer?.id || !offer.centerId) return null;
  return {
    offerId: offer.id,
    slug: seat.doctor.slug,
    centerId: String(offer.centerId),
    visitAt: offer.visitAt,
    requestCode: offer.requestCode,
    ...(bookId ? { bookId } : {}),
    savedAt: new Date().toISOString(),
  };
};

/**
 * Before the regular booking form books the held slot: remember it, so «نوبت‌های من» can tell the service
 * once the booking shows in the patient's list (the form itself knows nothing of the queue).
 */
export const rememberFormBooking = (seat: DabbehMembership) => {
  if (!isQueueApi()) return;
  const record = pendingOf(seat);
  if (record) savePendingBooking(record);
};

/**
 * Tells the service about held slots the patient has booked: records with a known book id at once, and —
 * with «نوبت‌های من»'s own list — any offered seat or pending record whose booking (same center, same time)
 * is in it. Safe to call often: each offer is reported once at a time, and `booked` is idempotent.
 */
export const reconcileBookings = async (books?: readonly BookedAppointment[]) => {
  if (!isQueueApi()) return;
  const targets = readPendingBookings().filter(record => isDabbehRolloutSlug(record.slug));
  for (const seat of Object.values(useDabbehStore.getState().memberships)) {
    if (seat.status !== 'offered' || !isDabbehRolloutSlug(seat.doctor.slug)) continue;
    const record = pendingOf(seat);
    if (record && !targets.some(item => item.offerId === record.offerId)) targets.push(record);
  }
  for (const target of targets) {
    const bookId = target.bookId ?? (books?.length ? findBookedAppointment(target, books) : null);
    if (bookId) await reportBooked(target.offerId, bookId, target.slug);
  }
};

/**
 * Keeps the seats in step with the service while a queue screen is open: at once, every 20 s while the
 * tab is visible, and when the patient comes back to the tab. `synced` turns true after the first answer
 * (or at once in the prototype / logged out), so a page can wait for it before deciding a seat is gone.
 */
export const useQueueSync = (active = true) => {
  const isLogin = useUserInfoStore(state => state.isLogin);
  const pending = useUserInfoStore(state => state.pending);
  const userId = useUserInfoStore(state => state.info?.id);
  const [synced, setSynced] = useState(!isQueueApi());
  useEffect(() => {
    if (!isQueueApi() || !active) return;
    if (!isLogin) {
      if (!pending) {
        // signed out (the site has finished loading the user): this device keeps nobody's seats
        forgetOtherAccount(null);
        setSynced(true);
      }
      return;
    }
    if (userId) forgetOtherAccount(String(userId));
    let cancelled = false;
    const run = () =>
      reconcileBookings()
        .catch(() => undefined)
        .then(() => syncQueueSeats())
        .catch(() => undefined)
        .finally(() => !cancelled && setSynced(true));
    run();
    const id = window.setInterval(() => document.visibilityState === 'visible' && run(), 20_000);
    window.addEventListener('focus', run);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener('focus', run);
    };
  }, [active, isLogin, pending, userId]);
  return synced;
};

/** People waiting in a doctor's queue, from the service (null in the prototype or when it cannot be read). */
export const fetchQueueSummary = async (slug: string) => {
  if (!isQueueApi()) return null;
  try {
    return await queueApi.summary(slug);
  } catch {
    return null;
  }
};

/** After paying (or with an active subscription): a seat in the draft doctor's queue. */
export const joinQueue = async ({ paid, viaSubscription }: { paid?: DabbehSubscription | null; viaSubscription: boolean }) => {
  const store = useDabbehStore.getState();
  const doctor = store.draft;
  if (!doctor) return null;
  if (!isQueueApi()) return viaSubscription ? store.joinWithSubscription(doctor) : store.purchase(paid);
  // back from the payment gateway the page may still be loading the account: the patient check needs it
  await loadedUser().catch(() => undefined);
  // «نوبت به نام» picked in the sheet — only by this same account (none: the service books for the owner)
  const patient = store.draftPatient && store.draftFor && store.draftFor === currentUserId() ? store.draftPatient : null;
  const seat = await queueApi.join(doctor.slug, patient);
  if (!viaSubscription) store.purchase(paid);
  return mirror(seat, viaSubscription);
};

export const leaveQueue = async (slug: string) => {
  if (isQueueApi()) await queueApi.leave(slug);
  changes += 1;
  useDabbehStore.getState().leave(slug);
};

/** «انصراف از این نوبت»: the appointment goes to the next person, the patient to the end of the line. */
export const passQueueOffer = async (seat: DabbehMembership) => {
  if (!isQueueApi() || !seat.offer?.id) return useDabbehStore.getState().passOffer(seat.doctor.slug);
  return mirror(await queueApi.pass(seat.offer.id));
};

/** Offers whose hold already ran out on this page: never ended twice (a sync may still show one held). */
const endedOffers = new Set<string>();

/**
 * The hold ran out on screen: show it at once (the place stays the service's last one), then take the
 * service's word for the new place. Once per offer — an offer already past its end is not ended again.
 */
export const holdEnded = (seat: DabbehMembership) => {
  const offer = seat.offer;
  const key = offer ? `${seat.doctor.slug}|${offer.id ?? offer.holdExpiresAt}` : null;
  if (!key || endedOffers.has(key)) return false;
  endedOffers.add(key);
  changes += 1;
  useDabbehStore.getState().passOffer(seat.doctor.slug, isQueueApi());
  // after the service's own expiry round (every 5 s), so the next answer already has the new place
  if (isQueueApi()) window.setTimeout(() => void syncQueueSeats().catch(() => undefined), 6_000);
  return true;
};

/** The logged-in user once the site has finished loading it (a tap right after the page opens must not miss it). */
export const loadedUser = (timeoutMs = 8_000) =>
  new Promise<{ isLogin: boolean; info: UserInfo }>(resolve => {
    const done = () => {
      const { isLogin, info } = useUserInfoStore.getState();
      resolve({ isLogin, info });
    };
    if (!useUserInfoStore.getState().pending) return done();
    const timer = window.setTimeout(() => {
      unsubscribe();
      done();
    }, timeoutMs);
    const unsubscribe = useUserInfoStore.subscribe(state => {
      if (state.pending) return;
      window.clearTimeout(timer);
      unsubscribe();
      done();
    });
  });

/**
 * The patient's own details are complete enough for the clinic to book without the booking form. Anything
 * missing (or a foreign national) goes through the regular booking form, on the same held slot.
 */
export const canBookDirectly = (user: UserInfo) =>
  !!user.id && !!user.name && !!user.family && !!user.cell && !!user.national_code && !isForeigner(user.is_foreigner);

/** The account says `true`; a saved person's list says `'1'` / `'0'` (a bare `!` would read `'0'` as foreign). */
const isForeigner = (value: unknown) => value === true || String(value) === '1';

/** No answer from the clinic within this: the same held slot goes on in the regular form, never a spinner forever. */
export const BOOK_TIMEOUT_MS = 25_000;

const withTimeout = <T>(promise: Promise<T>, ms: number) =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('book timeout')), ms);
    promise.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      error => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

/** The held time is not the one the patient saw (canvas TP4d): said above the new time on the card. */
const CHANGED_VISIT = 'زمان این نوبت تغییر کرده است؛ لطفاً پیش از تأیید، زمان جدید را ببینید.';
/** The clinic did not answer the booking in time (it may still have gone through). */
const LATE_ANSWER = 'پاسخ ثبت نوبت دیر رسید؛ چند لحظهٔ دیگر «نوبت‌های من» را دوباره ببینید.';

export type ConfirmResult =
  /** booked; open this address (receipt, factor or the payment gateway) */
  | { kind: 'done'; url: string; external?: boolean }
  /** the regular booking form for the held slot (prototype, missing details, or a clinic that asks more) */
  | { kind: 'form'; url: string; message?: string }
  /** the clinic's hold lapsed and the nearest free slot is a different time: shown on the card to confirm again */
  | { kind: 'changed'; message: string }
  /** the slot is gone and no other is free right now; the patient stays first in line */
  | { kind: 'gone'; message: string };

/**
 * «تأیید و دریافت نوبت»: books the held slot in the name locked on the seat with the site's regular booking
 * call (the same `/api/book` as the booking form), then tells the service. The clinic holds a slot for
 * 5 minutes; when that ran out, the service holds a fresh one for the same patient: the same time is booked
 * at once, a different time goes back to the card for the patient to confirm (never booked unseen).
 */
export const confirmQueueOffer = async (
  seat: DabbehMembership,
  user: UserInfo,
  { formUrl, isWebView, isApplication = false }: { formUrl: (seat: DabbehMembership) => string; isWebView: boolean; isApplication?: boolean },
): Promise<ConfirmResult> => {
  const toForm = (current: DabbehMembership, message?: string): ConfirmResult => {
    // the form books the slot without telling the service: «نوبت‌های من» does it once the booking shows
    rememberFormBooking(current);
    return { kind: 'form', url: formUrl(current), message };
  };
  if (!isQueueApi() || !seat.offer?.id) {
    useDabbehStore.getState().confirmOffer(seat.doctor.slug);
    return toForm(seat);
  }
  // the service takes the held slot again every few minutes (the clinic's hold lasts 5): book with the
  // request code it holds right now, not the one this page loaded
  // the service unreachable: the offer this page has; the service says there is no seat any more: gone
  const latest = await latestSeat(seat.doctor.slug).catch(() => undefined);
  if (latest === null) return { kind: 'gone', message: 'این نوبت دیگر در دسترس نیست. با آزاد شدن نوبت بعدی، دوباره خبرتان می‌کنیم.' };
  let current = latest ?? seat;
  if (!current.offer?.id) return { kind: 'gone', message: 'این نوبت دیگر در دسترس نیست. با آزاد شدن نوبت بعدی، دوباره خبرتان می‌کنیم.' };
  if (!sameVisit(current.offer, seat.offer))
    return { kind: 'changed', message: CHANGED_VISIT };
  if (!canBookDirectly(user)) return toForm(current);

  let renewedRetry = false;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const offer = current.offer!;
    if (!offer.requestCode || !offer.centerId || !offer.serverId || !offer.userCenterId) return toForm(current);
    const answer = await withTimeout(book({
      request_code: offer.requestCode,
      center_id: offer.centerId,
      server_id: offer.serverId,
      user_center_id: offer.userCenterId,
      // as the regular booking flow: the app shell gets the in-app payment, not the web one
      is_webview: isWebView || isApplication ? 1 : 0,
      first_name: user.name!,
      last_name: user.family!,
      // the account keeps the number without its 0; a saved person may have it with it
      cell: `0${String(user.cell).replace(/^0+/, "")}`,
      national_code: user.national_code!,
      selected_user_id: user.id!,
      is_foreigner: false,
      ...(user.gender && { gender: user.gender }),
      ...(user.father_name && { father_name: user.father_name }),
      ...(user.birth_date && { birth_date: user.birth_date }),
    } as Parameters<typeof book>[0]), BOOK_TIMEOUT_MS).catch(error => {
      if (error?.message !== 'book timeout') throw error;
      return null;
    });
    if (!answer) {
      // no answer in time — the booking may still land: never booked a second time through the form; the
      // list in «نوبت‌های من» (and the next sync) tells whether it went through
      void syncQueueSeats().catch(() => undefined);
      return { kind: 'gone', message: LATE_ANSWER };
    }
    const { data } = answer;

    if (data?.status === ClinicStatus.SUCCESS) {
      const bookId = String(data.book_info?.id ?? '');
      // the appointment is the patient's either way: the page moves on at once, and the service hears
      // `booked` in the background — kept as pending until it answers, so the next sync or visit of
      // «نوبت‌های من» tries again (idempotent on the service)
      const record = pendingOf(current, bookId || undefined);
      if (record) savePendingBooking(record);
      if (bookId) void reportBooked(offer.id!, bookId, seat.doctor.slug);
      changes += 1;
      useDabbehStore.getState().leave(seat.doctor.slug);
      const receipt = `/receipt/${offer.centerId}/${bookId}`;
      if (data.payment?.reqiure_payment === '1') {
        if (String(offer.serverId) === '1') return { kind: 'done', url: `/factor/${offer.centerId}/${bookId}` };
        // no gateway address: the receipt shows the booking and its payment state
        const redirect = data.payment?.redirect_url;
        if (!redirect) return { kind: 'done', url: receipt };
        return { kind: 'done', url: String(redirect), external: true };
      }
      return { kind: 'done', url: receipt };
    }

    if (data?.status === ClinicStatus.EXPIRE_TIME_SLOT && !renewedRetry) {
      // booked in the second the service was taking the same slot again: same time, new code
      renewedRetry = true;
      const latest = await latestSeat(seat.doctor.slug).catch(() => null);
      const renewed = latest?.offer;
      if (latest && renewed && renewed.id === offer.id && sameVisit(renewed, offer) && renewed.requestCode !== offer.requestCode) {
        current = latest;
        attempt -= 1;
        continue;
      }
    }

    if (data?.status === ClinicStatus.EXPIRE_TIME_SLOT && attempt === 0) {
      try {
        current = mirror(await queueApi.refresh(offer.id!));
        if (!current.offer?.id) break;
        if (!sameVisit(current.offer, offer))
          return { kind: 'changed', message: CHANGED_VISIT };
        continue;
      } catch (error) {
        if (error instanceof QueueApiError && error.status === 409) {
          void syncQueueSeats().catch(() => undefined);
          return { kind: 'gone', message: error.message };
        }
        throw error;
      }
    }
    // the clinic wants something the form asks for (insurance, details…): same slot, regular form
    return toForm(current, data?.message);
  }
  void syncQueueSeats().catch(() => undefined);
  return { kind: 'gone', message: 'این نوبت دیگر در دسترس نیست. با آزاد شدن نوبت بعدی، دوباره خبرتان می‌کنیم.' };
};
